import { useState, useEffect, useCallback } from 'react'
import { useApiClient } from '../contexts/ApiClientContext'
import type { RateLimitInfo } from '../lib/apiClient'

interface RateLimitStatus {
  isRateLimited: boolean
  rateLimitInfo?: RateLimitInfo
  timeUntilReset: {
    perMinute: number
    perHour: number
    perDay: number
  }
  canSendRequest: boolean
  mostRestrictiveLimit: 'minute' | 'hour' | 'day' | null
}

/**
 * Hook to manage rate limit state and provide UI feedback
 */
export function useRateLimit() {
  const apiClient = useApiClient()
  const [rateLimitStatus, setRateLimitStatus] = useState<RateLimitStatus>({
    isRateLimited: false,
    timeUntilReset: {
      perMinute: 0,
      perHour: 0,
      perDay: 0
    },
    canSendRequest: true,
    mostRestrictiveLimit: null
  })

  // Update rate limit status from API client
  const updateRateLimitStatus = useCallback(() => {
    if (!apiClient) return

    const rateLimitInfo = apiClient.getRateLimitInfo()
    if (!rateLimitInfo) {
      setRateLimitStatus(prev => ({
        ...prev,
        isRateLimited: false,
        canSendRequest: true,
        mostRestrictiveLimit: null
      }))
      return
    }

    const now = new Date()
    const timeUntilReset = {
      perMinute: Math.max(0, Math.ceil((rateLimitInfo.resetTime.perMinute.getTime() - now.getTime()) / 1000)),
      perHour: Math.max(0, Math.ceil((rateLimitInfo.resetTime.perHour.getTime() - now.getTime()) / 1000)),
      perDay: Math.max(0, Math.ceil((rateLimitInfo.resetTime.perDay.getTime() - now.getTime()) / 1000))
    }

    // Check if any limit is exhausted
    const hasRemainingTokens = {
      minute: rateLimitInfo.remaining.perMinute > 0,
      hour: rateLimitInfo.remaining.perHour > 0,
      day: rateLimitInfo.remaining.perDay > 0
    }

    const canSendRequest = hasRemainingTokens.minute && hasRemainingTokens.hour && hasRemainingTokens.day
    const isRateLimited = !canSendRequest

    // Find the most restrictive limit (the one that will reset soonest)
    let mostRestrictiveLimit: 'minute' | 'hour' | 'day' | null = null
    if (isRateLimited) {
      const resetTimes = [
        { type: 'minute' as const, time: timeUntilReset.perMinute, hasTokens: hasRemainingTokens.minute },
        { type: 'hour' as const, time: timeUntilReset.perHour, hasTokens: hasRemainingTokens.hour },
        { type: 'day' as const, time: timeUntilReset.perDay, hasTokens: hasRemainingTokens.day }
      ]
      
      // Find the limit that's exhausted and will reset soonest
      const exhaustedLimits = resetTimes.filter(limit => !limit.hasTokens)
      if (exhaustedLimits.length > 0) {
        mostRestrictiveLimit = exhaustedLimits.reduce((min, current) => 
          current.time < min.time ? current : min
        ).type
      }
    }

    setRateLimitStatus({
      isRateLimited,
      rateLimitInfo,
      timeUntilReset,
      canSendRequest,
      mostRestrictiveLimit
    })
  }, [apiClient])

  // Update status whenever API client changes or periodically
  useEffect(() => {
    updateRateLimitStatus()

    // Update every second to keep countdown accurate
    const interval = setInterval(updateRateLimitStatus, 1000)
    return () => clearInterval(interval)
  }, [updateRateLimitStatus])

  // Helper function to format time remaining
  const formatTimeRemaining = useCallback((seconds: number): string => {
    if (seconds <= 0) return '0s'
    
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`
    } else {
      return `${secs}s`
    }
  }, [])

  // Get user-friendly rate limit message
  const getRateLimitMessage = useCallback((): string | null => {
    if (!rateLimitStatus.isRateLimited || !rateLimitStatus.mostRestrictiveLimit) {
      return null
    }

    const timeRemainingKey = `per${rateLimitStatus.mostRestrictiveLimit.charAt(0).toUpperCase() + rateLimitStatus.mostRestrictiveLimit.slice(1)}` as keyof typeof rateLimitStatus.timeUntilReset
    const timeRemaining = rateLimitStatus.timeUntilReset[timeRemainingKey]
    const formattedTime = formatTimeRemaining(timeRemaining)
    
    switch (rateLimitStatus.mostRestrictiveLimit) {
      case 'minute':
        return `Rate limit reached. Try again in ${formattedTime}.`
      case 'hour':
        return `Hourly limit reached. Try again in ${formattedTime}.`
      case 'day':
        return `Daily limit reached. Try again in ${formattedTime}.`
      default:
        return 'Rate limit reached. Please try again later.'
    }
  }, [rateLimitStatus, formatTimeRemaining])

  // Get usage summary for display
  const getUsageSummary = useCallback((): string | null => {
    if (!rateLimitStatus.rateLimitInfo) return null

    const { remaining, limits } = rateLimitStatus.rateLimitInfo
    return `Usage: ${remaining.perMinute}/${limits.perMinute} per min, ${remaining.perHour}/${limits.perHour} per hour, ${remaining.perDay}/${limits.perDay} per day`
  }, [rateLimitStatus.rateLimitInfo])

  return {
    ...rateLimitStatus,
    updateRateLimitStatus,
    formatTimeRemaining,
    getRateLimitMessage,
    getUsageSummary
  }
} 